/**
 * What a drag hovering a FOLDER ROW of an FTP pane means — the Finder / VS Code
 * explorer / FileZilla rule:
 *
 *  - a drag from THIS pane onto one of its folders is a MOVE (existing);
 *  - on the REMOTE pane, files from the OS or a drag from the local pane onto a
 *    folder row UPLOAD INTO that folder — only that row lights up, not the
 *    whole list;
 *  - anything else (a file row, empty space) falls through to the pane, which
 *    targets the directory it is showing.
 *
 * Decided from the drag's TYPES, since `getData` is unreadable during dragover.
 * Pure, so the rule is tested rather than clicked through.
 */
export const INTERNAL_DND_MIME = 'application/x-apiboo-ftp';

/**
 * A second, EMPTY drag type whose NAME carries the source pane.
 *
 * `dataTransfer.getData` is deliberately blocked during `dragover` — only
 * `types` is readable — so there is no way to look inside the payload while
 * deciding whether to accept a drop. Without this a folder row could not tell a
 * same-pane MOVE from a cross-pane transfer, and would have to light up for
 * both and then refuse one on drop, which is the worst of the three options.
 */
export const dndSourceMime = (side) => `application/x-apiboo-ftp-from-${side}`;

/**
 * @param {object} a
 * @param {'local'|'remote'} a.side       the pane the row is in
 * @param {{path: string, isDir: boolean}} a.entry  the hovered row
 * @param {Iterable<string>} a.types      `dataTransfer.types`
 * @param {string[]} [a.selection]        the pane's selected paths
 * @param {boolean} [a.canMove]           the pane has a move handler
 * @param {boolean} [a.canDropInto]       the pane has an upload-into handler
 * @returns {'move'|'into'|null}
 */
export function rowDropKind({ side, entry, types, selection = [], canMove = false, canDropInto = false }) {
  if (!entry || !entry.isDir) return null;
  const list = Array.from(types || []);
  if (list.includes(dndSourceMime(side))) {
    // Never into itself (or a folder that is part of what is being dragged).
    return canMove && !selection.includes(entry.path) ? 'move' : null;
  }
  if (side !== 'remote' || !canDropInto) return null;
  const fromOtherPane = list.includes(INTERNAL_DND_MIME);
  const fromOs = list.includes('Files');
  return fromOtherPane || fromOs ? 'into' : null;
}
